import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  BarChart3, 
  Users, 
  DollarSign, 
  TrendingUp,
  CalendarDays,
  Layers
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Legend,
  PieChart,
  Pie
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MEETING_COLORS = {
  COM: '#3b82f6',
  AGM: '#10b981',
  SPECIAL: '#f59e0b'
};

const MeetingCalendar = ({ data }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(2026);

  // Fetch meetings data
  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        const response = await axios.get(`${API}/meetings`);
        setMeetings(response.data.meetings || []);
        
        // Auto-detect the year with most meetings
        const yearCounts = {};
        response.data.meetings?.forEach(m => {
          if (m.year) {
            yearCounts[m.year] = (yearCounts[m.year] || 0) + 1;
          }
        });
        const mostCommonYear = Object.entries(yearCounts).sort((a, b) => b[1] - a[1])[0];
        if (mostCommonYear) {
          setSelectedYear(parseInt(mostCommonYear[0]));
        }
      } catch (error) {
        console.error('Failed to fetch meetings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMeetings();
  }, []);

  // Get available years
  const availableYears = useMemo(() => {
    const years = new Set(meetings.map(m => m.year).filter(Boolean));
    return Array.from(years).sort();
  }, [meetings]);

  // Calculate manager meeting stats - filtered by selected year
  const managerMeetingStats = useMemo(() => {
    const stats = {};
    
    // Filter meetings by selected year first
    const yearFilteredMeetings = meetings.filter(m => m.year === selectedYear);
    
    yearFilteredMeetings.forEach(meeting => {
      const manager = meeting.manager || 'Unknown';
      if (!stats[manager]) {
        stats[manager] = {
          name: manager,
          total: 0,
          com: 0,
          agm: 0,
          special: 0,
          totalCost: 0,
          byMonth: Array(12).fill(0)
        };
      }
      
      stats[manager].total += 1;
      stats[manager].totalCost += meeting.meetingCost || 0;
      
      if (meeting.meetingType === 'COM') stats[manager].com += 1;
      else if (meeting.meetingType === 'AGM') stats[manager].agm += 1;
      else if (meeting.meetingType === 'SPECIAL') stats[manager].special += 1;
      
      // Track by month (already filtered by year above)
      if (meeting.month) {
        stats[manager].byMonth[meeting.month - 1] += 1;
      }
    });
    
    return Object.values(stats).sort((a, b) => b.total - a.total);
  }, [meetings, selectedYear]);

  // Monthly meeting data for selected year
  const monthlyData = useMemo(() => {
    const data = MONTHS.map((month, idx) => ({
      month,
      COM: 0,
      AGM: 0,
      SPECIAL: 0,
      total: 0
    }));
    
    meetings.forEach(meeting => {
      if (meeting.year === selectedYear && meeting.month) {
        const idx = meeting.month - 1;
        if (idx >= 0 && idx < 12) {
          data[idx].total += 1;
          if (meeting.meetingType === 'COM') data[idx].COM += 1;
          else if (meeting.meetingType === 'AGM') data[idx].AGM += 1;
          else if (meeting.meetingType === 'SPECIAL') data[idx].SPECIAL += 1;
        }
      }
    });
    
    return data;
  }, [meetings, selectedYear]);

  // Meeting type distribution
  const meetingTypeData = useMemo(() => {
    const counts = { COM: 0, AGM: 0, SPECIAL: 0 };
    meetings.forEach(m => {
      if (m.meetingType && counts[m.meetingType] !== undefined) {
        counts[m.meetingType] += 1;
      }
    });
    return [
      { name: 'Committee (COM)', value: counts.COM, color: MEETING_COLORS.COM },
      { name: 'AGM', value: counts.AGM, color: MEETING_COLORS.AGM },
      { name: 'Special', value: counts.SPECIAL, color: MEETING_COLORS.SPECIAL }
    ];
  }, [meetings]);

  // Heatmap data for Gantt-style visualization
  const heatmapData = useMemo(() => {
    const topManagers = managerMeetingStats.slice(0, 15);
    return topManagers.map(manager => ({
      name: manager.name,
      ...MONTHS.reduce((acc, m, idx) => {
        acc[m] = manager.byMonth[idx];
        return acc;
      }, {})
    }));
  }, [managerMeetingStats]);

  // Calculate totals
  const totals = useMemo(() => {
    return {
      totalMeetings: meetings.length,
      totalCost: meetings.reduce((sum, m) => sum + (m.meetingCost || 0), 0),
      comCount: meetings.filter(m => m.meetingType === 'COM').length,
      agmCount: meetings.filter(m => m.meetingType === 'AGM').length,
      specialCount: meetings.filter(m => m.meetingType === 'SPECIAL').length
    };
  }, [meetings]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`p-4 rounded-xl border shadow-xl ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
          <p className={`font-bold text-sm mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{label}</p>
          <div className="space-y-1 text-xs">
            {payload.map((entry) => (
              <div key={entry.name} className="flex justify-between gap-4">
                <span style={{ color: entry.color }}>{entry.name}:</span>
                <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Get color intensity for heatmap
  const getHeatmapColor = (value, max) => {
    if (value === 0) return isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)';
    const intensity = Math.min(value / (max || 1), 1);
    return `rgba(16, 185, 129, ${0.2 + intensity * 0.6})`;
  };

  const maxMeetingsInCell = useMemo(() => {
    return Math.max(...heatmapData.flatMap(row => MONTHS.map(m => row[m] || 0)));
  }, [heatmapData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (meetings.length === 0) {
    return (
      <div className={`p-8 rounded-2xl border text-center ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200'}`}>
        <Calendar className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
        <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>No Meeting Data</h3>
        <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          Upload a meetings CSV file to see meeting calendar analytics
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/10' : 'bg-blue-100'}`}>
              <Calendar className="w-5 h-5 text-blue-500" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total Meetings</span>
          </div>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{totals.totalMeetings.toLocaleString()}</p>
        </div>

        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-100'}`}>
              <DollarSign className="w-5 h-5 text-emerald-500" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total Cost</span>
          </div>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>${totals.totalCost.toLocaleString()}</p>
        </div>

        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg bg-blue-500/10`}>
              <Layers className="w-5 h-5 text-blue-500" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>COM Meetings</span>
          </div>
          <p className={`text-3xl font-bold text-blue-500`}>{totals.comCount.toLocaleString()}</p>
        </div>

        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg bg-emerald-500/10`}>
              <CalendarDays className="w-5 h-5 text-emerald-500" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>AGM Meetings</span>
          </div>
          <p className={`text-3xl font-bold text-emerald-500`}>{totals.agmCount.toLocaleString()}</p>
        </div>

        <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg bg-amber-500/10`}>
              <BarChart3 className="w-5 h-5 text-amber-500" />
            </div>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Special</span>
          </div>
          <p className={`text-3xl font-bold text-amber-500`}>{totals.specialCount.toLocaleString()}</p>
        </div>
      </div>

      {/* Year Selector */}
      <div className="flex items-center gap-4">
        <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Select Year:</span>
        <div className="flex gap-2">
          {availableYears.map(year => (
            <button
              key={year}
              onClick={() => setSelectedYear(year)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${selectedYear === year 
                  ? 'bg-emerald-500 text-white' 
                  : isDark 
                    ? 'bg-white/5 text-slate-400 hover:bg-white/10' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Monthly Stacked Bar Chart */}
        <div className={`xl:col-span-2 p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <h3 className={`text-lg font-bold mb-1 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <BarChart3 className="w-5 h-5 mr-2 text-blue-500" />
            Monthly Meeting Distribution ({selectedYear})
          </h3>
          <p className={`text-xs mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Meetings by type per month</p>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthlyData} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
                <XAxis dataKey="month" tick={{fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b'}} axisLine={false} tickLine={false} />
                <YAxis tick={{fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b'}} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="COM" stackId="a" fill={MEETING_COLORS.COM} radius={[0, 0, 0, 0]} name="Committee" />
                <Bar dataKey="AGM" stackId="a" fill={MEETING_COLORS.AGM} radius={[0, 0, 0, 0]} name="AGM" />
                <Bar dataKey="SPECIAL" stackId="a" fill={MEETING_COLORS.SPECIAL} radius={[4, 4, 0, 0]} name="Special" />
                <Line type="monotone" dataKey="total" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444', r: 4 }} name="Total" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Meeting Type Pie Chart */}
        <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <h3 className={`text-lg font-bold mb-1 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Layers className="w-5 h-5 mr-2 text-emerald-500" />
            Meeting Type Distribution
          </h3>
          <p className={`text-xs mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>All time breakdown</p>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={meetingTypeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {meetingTypeData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [value, name]}
                  contentStyle={{
                    background: isDark ? '#1a1a1c' : 'white',
                    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                    borderRadius: '12px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 -mt-4">
              {meetingTypeData.map((entry) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{entry.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Heatmap / Gantt Style View */}
      <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
        <h3 className={`text-lg font-bold mb-1 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <Users className="w-5 h-5 mr-2 text-purple-500" />
          Manager Meeting Saturation ({selectedYear})
        </h3>
        <p className={`text-xs mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Meeting density by manager across months (darker = more meetings)</p>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Manager
                </th>
                {MONTHS.map(month => (
                  <th key={month} className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {month}
                  </th>
                ))}
                <th className={`px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Total
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-slate-100'}`}>
              {heatmapData.map((row) => {
                const rowTotal = MONTHS.reduce((sum, m) => sum + (row[m] || 0), 0);
                return (
                  <tr key={row.name} className={`transition-colors ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50'}`}>
                    <td className={`px-3 py-3 font-medium truncate max-w-[150px] ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {row.name}
                    </td>
                    {MONTHS.map(month => (
                      <td key={month} className="px-1 py-2 text-center">
                        <div 
                          className="w-8 h-8 mx-auto rounded-md flex items-center justify-center text-xs font-semibold transition-all"
                          style={{ 
                            backgroundColor: getHeatmapColor(row[month] || 0, maxMeetingsInCell),
                            color: row[month] > 0 ? (isDark ? 'white' : '#1e293b') : (isDark ? '#475569' : '#94a3b8')
                          }}
                        >
                          {row[month] || '-'}
                        </div>
                      </td>
                    ))}
                    <td className={`px-3 py-3 text-right font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      {rowTotal}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Heatmap Legend */}
        <div className="flex items-center justify-end gap-2 mt-4">
          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Less</span>
          <div className="flex gap-1">
            {[0, 0.25, 0.5, 0.75, 1].map((intensity) => (
              <div 
                key={intensity}
                className="w-4 h-4 rounded"
                style={{ backgroundColor: `rgba(16, 185, 129, ${0.1 + intensity * 0.7})` }}
              />
            ))}
          </div>
          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>More</span>
        </div>
      </div>

      {/* Top Managers by Meetings */}
      <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
        <h3 className={`text-lg font-bold mb-1 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <TrendingUp className="w-5 h-5 mr-2 text-amber-500" />
          Top Managers by Meeting Workload
        </h3>
        <p className={`text-xs mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Managers with highest meeting counts</p>
        <div className="h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={managerMeetingStats.slice(0, 10)} 
              layout="vertical" 
              margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
              <XAxis type="number" tick={{fontSize: 10, fill: '#64748b'}} axisLine={false} tickLine={false} />
              <YAxis dataKey="name" type="category" width={120} tick={{fontSize: 11, fill: isDark ? '#94a3b8' : '#475569'}} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="com" stackId="a" fill={MEETING_COLORS.COM} name="COM" radius={[0, 0, 0, 0]} />
              <Bar dataKey="agm" stackId="a" fill={MEETING_COLORS.AGM} name="AGM" radius={[0, 0, 0, 0]} />
              <Bar dataKey="special" stackId="a" fill={MEETING_COLORS.SPECIAL} name="Special" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default MeetingCalendar;
;
